import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { getCartToken } from "@faire/retailer-visitor-shared/serialized-data/getCartToken";
import { useSettingPrefetchPdpApis } from "@faire/retailer-visitor-shared/settings/useSettingFEPrefetchPdpApis";
import { useAssignSettingPrefetchPdpApis } from "@faire/retailer-visitor-shared/settings/useSettingFEPrefetchPdpApis";
import { PreloadedImageTracker } from "@faire/retailer-visitor-shared/views/product/preload/PreloadedImageTracker";
import { buildProductImageGridSrcSet, IMAGE_GRID_MAX_IMAGES_VISIBLE } from "@faire/retailer-visitor-shared/views/product/ProductImages/utils";
import { prefetchLeanProduct } from "@faire/retailer-visitor-shared/views/product/utils/useLeanProductQuery";
import { getWindowOrThrow } from "@faire/web--source/common/globals/getWindow";
import { getQueryKey as getProductReviewsQueryKey, getQueryFn as getProductReviewsQueryFn } from "@faire/web-api--source/api/product/productToken/brand-reviews-summary/get-hooks";
import { getQueryKey as getProductPromotionsQueryKey, getQueryFn as getProductPromotionsQueryFn } from "@faire/web-api--source/api/product/productToken/product-promo-messages/get-hooks";
import fetchCart from "@faire/web-api--source/api/v2/carts/cartToken/get";
import { getQueryKey as getCartQueryKey } from "@faire/web-api--source/api/v2/carts/cartToken/get-hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { getCartQueryParams } from "../../brand/BrandCart/hooks/useBrandCart";
export function usePreloadNavigationToPDP() {
    var queryClient = useQueryClient();
    var assignSettingPrefetchPdpApis = useAssignSettingPrefetchPdpApis();
    var prefetchPdpApis = useSettingPrefetchPdpApis();
    var isLoggedInRetailer = useIsLoggedInRetailer();
    return useCallback(function(product) {
        assignSettingPrefetchPdpApis();
        preloadNavigationToPDP(queryClient, product, isLoggedInRetailer, prefetchPdpApis);
    }, [
        queryClient,
        assignSettingPrefetchPdpApis,
        prefetchPdpApis,
        isLoggedInRetailer
    ]);
}
function preloadNavigationToPDP(queryClient, product, isLoggedInRetailer, prefetchPdpApis) {
    prefetchProductImages(product);
    if (product.productToken) {
        // Prefetch product details
        prefetchLeanProduct(queryClient, product.productToken);
        if (prefetchPdpApis) {
            // Prefetch product promotions
            queryClient.prefetchQuery({
                queryKey: getProductPromotionsQueryKey(product.productToken),
                queryFn: getProductPromotionsQueryFn(product.productToken)
            });
            // Prefetch product reviews
            queryClient.prefetchQuery({
                queryKey: getProductReviewsQueryKey(product.productToken),
                queryFn: getProductReviewsQueryFn(product.productToken)
            });
        }
    }
    if (prefetchPdpApis && product.brandToken && isLoggedInRetailer) {
        // Prefetch cart for logged in retailer
        var params = getCartQueryParams(product.brandToken);
        queryClient.prefetchQuery({
            queryKey: getCartQueryKey(getCartToken(), params),
            queryFn: function() {
                return fetchCart(getCartToken(), params);
            }
        });
    }
}
function prefetchProductImages(product) {
    PreloadedImageTracker.get().clear();
    var Image = getWindowOrThrow().Image;
    var showVerticalImagery = product.showVerticalImagery || false;
    getUniqueImages(product).forEach(function(image) {
        var _buildProductImageGridSrcSet = buildProductImageGridSrcSet(image, showVerticalImagery), src = _buildProductImageGridSrcSet.src, srcSet = _buildProductImageGridSrcSet.srcSet;
        if (!src || !srcSet) {
            return;
        }
        var img = new Image();
        img.srcset = srcSet;
        img.src = src;
        PreloadedImageTracker.get().setInfo(src, {
            img: img,
            showVerticalImagery: showVerticalImagery
        });
    });
}
function getUniqueImages(product) {
    var images = product.images ? _to_consumable_array(product.images) : [];
    var bestImage = product.bestImage;
    if ((bestImage === null || bestImage === void 0 ? void 0 : bestImage.url) && images.every(function(image) {
        return image.url !== bestImage.url;
    })) {
        images.unshift(bestImage);
    }
    return images.slice(0, IMAGE_GRID_MAX_IMAGES_VISIBLE);
}
